import Decimal from 'decimal.js';

Decimal.set({ precision: 30, rounding: Decimal.ROUND_HALF_UP });

export type Money = string;

/**
 * Единая точность всех денежных величин в системе: 4 знака после запятой.
 * Применяется к суммам, курсам, USDT-эквивалентам, профиту. Округление —
 * математическое (HALF_UP) через глобальный `Decimal.set` выше.
 *
 * Frontend визуально обрезает trailing zeros через `normalizeMoney`/`fmtAmt`.
 */
export const MONEY_SCALE = 4;

/**
 * UI-точности (формы и отображение) — отдельно от MONEY_SCALE (хранение):
 * суммы вводятся/показываются с 2 знаками (как в прототипе), курсы — с 4.
 * step-атрибуты HTML-инпутов выводятся из этих же значений — единый источник.
 */
export const AMT_UI_SCALE = 2;
export const RATE_UI_SCALE = 4;
export const AMT_UI_STEP = (10 ** -AMT_UI_SCALE).toFixed(AMT_UI_SCALE); // '0.01'
export const RATE_UI_STEP = (10 ** -RATE_UI_SCALE).toFixed(RATE_UI_SCALE); // '0.0001'

export function D(value: Money | number | Decimal): Decimal {
  if (value instanceof Decimal) return value;
  return new Decimal(value);
}

/** Округляет значение до MONEY_SCALE знаков (HALF_UP). Возвращает строку с
 *  trailing zeros (для записи в БД). Для отображения — `normalizeMoney`. */
export function quantize(value: Money | number | Decimal): Money {
  return D(value).toDecimalPlaces(MONEY_SCALE).toFixed(MONEY_SCALE);
}

export function toMoney(value: Money | number | Decimal): Money {
  return D(value).toString();
}

export function negate(value: Money): Money {
  return D(value).neg().toString();
}

export function isZero(value: Money): boolean {
  return D(value).isZero();
}

export function eq(a: Money, b: Money): boolean {
  return D(a).eq(D(b));
}

/** Нормализует money-строку, убирая trailing zeros: '15000.00000000' → '15000', '0.10' → '0.1'. */
export function normalizeMoney(value: Money | null | undefined): Money | null {
  if (value === null || value === undefined) return null;
  return D(value).toString();
}
